// src/component/CurrentBuildAdmin.js
// JSXあり / JS（TSなし）
// 依存: react, @mui/material, firebase v9
//
// 目的:
// - appFlags/currentBuild を編集（required/minSupported/rollout/message）
// - appMetrics/buildDistribution を購読して buildId 分布を表示
//
// ポイント:
// - buildDistribution が 0 に見える問題は「normalize」より先に
//   ① onSnapshot が読めているか（permission-denied等）を可視化するのが先。
// - Functions は触らず、UI 側で “読めてる/読めてない” と
//   “byBuildId がネストかフラットか” を両対応で正規化する。
// - Firestore コンソールで「byBuildId.2.0」がトップレベルに見える場合でも
//   実体は (A) byBuildId: { "2": { "0": ... } } のネスト か
//         (B) "byBuildId.2.0": ... が本当にトップレベルキー か
//   の2系統があり得る。両方拾う。

import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Divider,
  Chip,
  Alert,
  CircularProgress,
} from "@mui/material";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { doc, getDoc, setDoc, serverTimestamp, onSnapshot } from "firebase/firestore";
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { db } from "../api/firebase";
import { AppThemeContext } from "./AppShell";

/**
 * =========================================================
 * CurrentBuildAdmin（運用コメント）
 * =========================================================
 *
 * 【この画面の役割】
 * 1) appFlags/currentBuild を編集して BuildIdGuard の判定基準をコントロールする
 * 2) appMetrics/buildDistribution を購読して buildId 分布（旧JS残党の残量）を軽量に観測する
 *
 * 【重要】
 * - buildDistribution は「users/{uid}.buildId が変化したときのみ」Functions が更新する想定
 * - ここが 0 の場合、最初に疑うべきは
 *    (A) 画面が appMetrics/buildDistribution を“読めていない”（permission-denied / パス違い）
 *    (B) そもそも Functions が書けていない（今回は“書けてる”スクショがあるので(A)濃厚）
 *
 * 【この実装の方針】
 * - Functions 側の構造には手を入れず
 * - onSnapshot の成功/失敗を toast と console で可視化して原因を確定する
 * - byBuildId の形式差（ネスト / フラット）を UI 側で正規化して表示する
 * =========================================================
 */

// 例: "3.0" -> 300 / "3.12" -> 312 / "3" -> 300
const buildCodeFromId = (id) => {
  const s = String(id ?? "").trim();
  if (!s) return 0;

  const parts = s.split(".");
  const major = Number(parts[0] ?? 0);
  const minorRaw = parts[1] ?? "0";

  // minor を2桁想定で丸める（"1" -> 10, "12"->12, "123"->12）
  const minor = Number(String(minorRaw).padEnd(2, "0").slice(0, 2));

  if (!Number.isFinite(major) || !Number.isFinite(minor)) return 0;
  return major * 100 + minor;
};

const clampPercent = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.floor(n)));
};

const normalizeUidList = (v) => {
  if (Array.isArray(v)) return v.map((x) => String(x).trim()).filter(Boolean);
  const s = String(v ?? "").trim();
  if (!s) return [];
  // 改行/カンマ/スペース区切り全部対応
  return s
    .split(/[\s,]+/g)
    .map((x) => String(x).trim())
    .filter(Boolean);
};

const uidListToText = (arr) => (Array.isArray(arr) ? arr.join("\n") : "");

const DEFAULT_DOC = {
  requiredBuildId: "2.0",
  requiredBuildCode: 200,
  minSupportedBuildId: "2.0",
  minSupportedBuildCode: 200,
  rollout: {
    enabled: true,
    percent: 100,
    allowUids: [],
    denyUids: [],
  },
  message: {
    title: "アップデートのお願い",
    body: "最新版へ更新してください。更新後、再度アクセスできます。",
    primaryText: "更新する",
    secondaryText: "あとで",
  },
};

export default function CurrentBuildAdmin() {
  const theme = useContext(AppThemeContext) || {};
  const accent = theme.railActive ?? "#5A35FF";
  const surface = theme.surfaceBg ?? "#FFF";
  const border = theme.bottomBorder ?? "rgba(0,0,0,0.08)";
  const textBase = theme.railText ?? "rgba(40,40,60,0.9)";

  // ------------------------------------------------------------
  // currentBuild（制御Doc）
  // ------------------------------------------------------------
  const ref = useMemo(() => doc(db, "appFlags", "currentBuild"), []);
  const [loading, setLoading] = useState(true);
  const [liveDoc, setLiveDoc] = useState(null); // Firestoreの現物
  const [form, setForm] = useState(DEFAULT_DOC); // 編集中
  const [dirty, setDirty] = useState(false);

  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ type: "", text: "" });

  // ------------------------------------------------------------
  // buildDistribution（集計Doc）: appMetrics/buildDistribution
  // ------------------------------------------------------------
  const metricsRef = useMemo(() => doc(db, "appMetrics", "buildDistribution"), []);
  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(true);

  // ------------------------------------------------------------
  // currentBuild をリアルタイム購読（管理画面なので onSnapshot 推奨）
  // ------------------------------------------------------------
  useEffect(() => {
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const d = snap.exists() ? snap.data() : null;
        const merged = deepMerge(DEFAULT_DOC, d || {});

        // 既存Docが古い場合でも code を補完
        merged.requiredBuildId = String(merged.requiredBuildId ?? "").trim() || "2.0";
        merged.minSupportedBuildId = String(merged.minSupportedBuildId ?? "").trim() || "2.0";

        merged.requiredBuildCode =
          typeof merged.requiredBuildCode === "number" && Number.isFinite(merged.requiredBuildCode)
            ? merged.requiredBuildCode
            : buildCodeFromId(merged.requiredBuildId);

        merged.minSupportedBuildCode =
          typeof merged.minSupportedBuildCode === "number" && Number.isFinite(merged.minSupportedBuildCode)
            ? merged.minSupportedBuildCode
            : buildCodeFromId(merged.minSupportedBuildId);

        merged.rollout = merged.rollout || {};
        merged.rollout.enabled = Boolean(merged.rollout.enabled);
        merged.rollout.percent = clampPercent(merged.rollout.percent);
        merged.rollout.allowUids = normalizeUidList(merged.rollout.allowUids);
        merged.rollout.denyUids = normalizeUidList(merged.rollout.denyUids);

        merged.message = merged.message || {};
        merged.message.title = String(merged.message.title ?? "").trim();
        merged.message.body = String(merged.message.body ?? "").trim();
        merged.message.primaryText = String(merged.message.primaryText ?? "").trim();
        merged.message.secondaryText = String(merged.message.secondaryText ?? "").trim();

        setLiveDoc(merged);

        // 変更中に上書きされるのを避ける：dirtyでない時だけフォームへ反映
        setForm((prev) => (dirty ? prev : merged));
        setLoading(false);
      },
      (err) => {
        console.warn("[currentBuild] onSnapshot error:", err);
        setLoading(false);
        setToast({
          type: "error",
          text: `currentBuild 読み込み失敗: ${err?.code || ""} ${err?.message || err}`,
        });
      }
    );

    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, dirty]);

  // ------------------------------------------------------------
  // buildDistribution を購読（users 全読込はしない）
  // ★ここで “読めてない” を toast と console で可視化する
  // ------------------------------------------------------------
  useEffect(() => {
    const unsub = onSnapshot(
      metricsRef,
      (snap) => {
        const data = snap.exists() ? snap.data() : null;

        // console.log("[buildDistribution] path:", metricsRef.path);
        // console.log("[buildDistribution] exists:", snap.exists(), "data:", data);

        setMetrics(data);
        setMetricsLoading(false);
      },
      (err) => {
        console.warn("[buildDistribution] snapshot error:", err);

        setMetrics(null);
        setMetricsLoading(false);

        setToast({
          type: "error",
          text: `buildDistribution 読み取りエラー: ${err?.code || ""} ${err?.message || err}`,
        });
      }
    );

    return () => unsub();
  }, [metricsRef]);

  const setField = (path, value) => {
    setDirty(true);
    setForm((prev) => setByPath(prev, path, value));
  };

  // ------------------------------------------------------------
  // 入力→自動でbuildCode補完（required/minSupported）
  // ------------------------------------------------------------
  useEffect(() => {
    const reqId = String(form.requiredBuildId ?? "").trim();
    const minId = String(form.minSupportedBuildId ?? "").trim();

    const reqCode = buildCodeFromId(reqId);
    const minCode = buildCodeFromId(minId);

    setForm((prev) => {
      const next = { ...prev };

      if (reqId !== prev.requiredBuildId) next.requiredBuildId = reqId;
      if (minId !== prev.minSupportedBuildId) next.minSupportedBuildId = minId;

      if (reqCode && reqCode !== prev.requiredBuildCode) next.requiredBuildCode = reqCode;
      if (minCode && minCode !== prev.minSupportedBuildCode) next.minSupportedBuildCode = minCode;

      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.requiredBuildId, form.minSupportedBuildId]);

  const validate = () => {
    const errors = [];

    const requiredId = String(form.requiredBuildId ?? "").trim();
    const minId = String(form.minSupportedBuildId ?? "").trim();
    const requiredCode = Number(form.requiredBuildCode);
    const minCode = Number(form.minSupportedBuildCode);

    if (!requiredId) errors.push("requiredBuildId が空です");
    if (!minId) errors.push("minSupportedBuildId が空です");
    if (!Number.isFinite(requiredCode) || requiredCode <= 0) errors.push("requiredBuildCode が不正です");
    if (!Number.isFinite(minCode) || minCode <= 0) errors.push("minSupportedBuildCode が不正です");

    if (Number.isFinite(requiredCode) && Number.isFinite(minCode) && minCode > requiredCode) {
      errors.push("minSupported は required より大きくできません（minSupported <= required）");
    }

    const p = clampPercent(form?.rollout?.percent);
    if (p < 0 || p > 100) errors.push("rollout.percent は 0〜100 です");

    return errors;
  };

  const handleReload = async () => {
    setToast({ type: "", text: "" });
    setLoading(true);

    try {
      const snap = await getDoc(ref);
      const d = snap.exists() ? snap.data() : null;
      const merged = deepMerge(DEFAULT_DOC, d || {});
      setLiveDoc(merged);
      setForm(merged);
      setDirty(false);
    } catch (e) {
      console.warn("[currentBuild] reload failed:", e);
      setToast({ type: "error", text: "再読み込みに失敗しました。" });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setToast({ type: "", text: "" });

    const errors = validate();
    if (errors.length) {
      setToast({ type: "error", text: errors.join(" / ") });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        requiredBuildId: String(form.requiredBuildId ?? "").trim(),
        requiredBuildCode: buildCodeFromId(form.requiredBuildId),

        minSupportedBuildId: String(form.minSupportedBuildId ?? "").trim(),
        minSupportedBuildCode: buildCodeFromId(form.minSupportedBuildId),

        rollout: {
          enabled: Boolean(form?.rollout?.enabled),
          percent: clampPercent(form?.rollout?.percent),
          allowUids: normalizeUidList(form?.rollout?.allowUids),
          denyUids: normalizeUidList(form?.rollout?.denyUids),
        },

        message: {
          title: String(form?.message?.title ?? "").trim(),
          body: String(form?.message?.body ?? "").trim(),
          primaryText: String(form?.message?.primaryText ?? "").trim(),
          secondaryText: String(form?.message?.secondaryText ?? "").trim(),
        },

        updatedAt: serverTimestamp(),
      };

      await setDoc(ref, payload, { merge: true });
      setDirty(false);
      setToast({ type: "success", text: "保存しました。" });
    } catch (e) {
      console.warn("[currentBuild] save failed:", e);
      setToast({ type: "error", text: `保存に失敗しました: ${e?.message || e}` });
    } finally {
      setSaving(false);
    }
  };

  const liveSummary = useMemo(() => {
    if (!liveDoc) return null;
    return {
      required: `${liveDoc.requiredBuildId} (${liveDoc.requiredBuildCode})`,
      minSupported: `${liveDoc.minSupportedBuildId} (${liveDoc.minSupportedBuildCode})`,
      rollout: `${liveDoc.rollout?.enabled ? "ON" : "OFF"} / ${clampPercent(liveDoc.rollout?.percent)}%`,
      allow: (liveDoc.rollout?.allowUids || []).length,
      deny: (liveDoc.rollout?.denyUids || []).length,
    };
  }, [liveDoc]);

  // ------------------------------------------------------------
  // buildDistribution を整形（Functions 側の形式差に両対応）
  // ------------------------------------------------------------
  const buildDist = useMemo(() => {
    const by = normalizeBuildDistribution(metrics || {});

    const entries = Object.entries(by)
      .map(([k, v]) => [k, Number(v || 0)])
      .filter(([, v]) => Number.isFinite(v))
      .sort(([a], [b]) => {
        const aUnknown = a === "unknown";
        const bUnknown = b === "unknown";
        if (aUnknown && !bUnknown) return 1;
        if (!aUnknown && bUnknown) return -1;
        return buildCodeFromId(b) - buildCodeFromId(a);
      });

    const total = entries.reduce((s, [, v]) => s + v, 0);
    return { entries, total, by };
  }, [metrics]);

  return (
    <Box sx={{ p: { xs: 2.5, sm: 3.5 }, maxWidth: 920, mx: "auto" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ mb: 2.2 }}>
        <Box>
          <Typography
            variant="overline"
            sx={{ letterSpacing: "0.18em", fontSize: 11, opacity: 0.8, color: textBase }}
          >
            ADMIN / APP FLAGS
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 900, letterSpacing: "0.02em", color: textBase }}>
            BuildID 制御（currentBuild）
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.6, color: "rgba(0,0,0,0.6)" }}>
            強制アップデートの基準と文言、ロールアウトを設定します。
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.2} alignItems="center">
          <Button
            variant="outlined"
            onClick={handleReload}
            startIcon={<RefreshRoundedIcon />}
            disabled={loading || saving}
            sx={{ borderRadius: 999 }}
          >
            再読み込み
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            startIcon={saving ? <CircularProgress size={18} /> : <SaveRoundedIcon />}
            disabled={loading || saving}
            sx={{ borderRadius: 999, bgcolor: accent, "&:hover": { bgcolor: accent } }}
          >
            保存
          </Button>
        </Stack>
      </Stack>

      {toast.text && (
        <Alert severity={toast.type === "success" ? "success" : "error"} sx={{ mb: 2 }}>
          {toast.text}
        </Alert>
      )}

      {/* ===============================
          BuildID 分布（利用状況）
          - users 全読込はしない
          - appMetrics/buildDistribution 1doc だけ購読
      =============================== */}
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          p: 2,
          borderRadius: 3,
          bgcolor: surface,
          border: `1px solid ${border}`,
          boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
        }}
      >
        <Stack spacing={1.2}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
            <Chip icon={<InfoOutlinedIcon />} label="Build 利用状況" variant="outlined" />
            <Chip label={`total: ${buildDist.total}`} />
            <Chip label={`2.0: ${buildDist.by?.["2.0"] || 0}`} color="warning" />
            <Chip label={`unknown: ${buildDist.by?.unknown || 0}`} />
          </Stack>

          {metricsLoading ? (
            <Box sx={{ py: 1 }}>
              <CircularProgress size={22} />
            </Box>
          ) : buildDist.entries.length ? (
            <Box sx={{ width: "100%", height: 280, pt: 1 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={buildDist.entries.map(([bid, cnt]) => ({ buildId: bid, count: cnt }))}
                  margin={{ top: 24, right: 16, left: 0, bottom: 8 }}
                >
                  <XAxis
                    dataKey="buildId"
                    tick={{ fontSize: 12, fill: "rgba(0,0,0,0.7)" }}
                    axisLine={{ stroke: border }}
                    tickLine={false}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 12, fill: "rgba(0,0,0,0.5)" }}
                    axisLine={{ stroke: border }}
                    tickLine={false}
                    width={40}
                  />
                  <RechartsTooltip
                    cursor={{ fill: "rgba(0,0,0,0.04)" }}
                    formatter={(value) => [`${value} 人`, "ユーザー数"]}
                    labelFormatter={(label) => `BuildID: ${label}`}
                  />
                  <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                    {buildDist.entries.map(([bid]) => (
                      <Cell key={bid} fill={bid === "2.0" ? "#ED6C02" : accent} />
                    ))}
                    <LabelList
                      dataKey="count"
                      position="top"
                      style={{ fontSize: 11, fill: "rgba(0,0,0,0.7)" }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          ) : (
            <Alert severity="warning" sx={{ width: "100%" }}>
              appMetrics/buildDistribution が未作成、または分布が空です。
              （または読み取り権限がなく metrics が取れていません）
              toast / console を確認してください。
            </Alert>
          )}

          <Alert severity="info">
            この分布は <b>users/{`{uid}`}.buildId が変化したときのみ</b> 更新されます。
            ここが動き始めれば「旧JS残党がどれだけ残っているか」を軽量に追跡できます。
          </Alert>
        </Stack>
      </Paper>

      {/* 現在の反映状態 */}
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          p: 2,
          borderRadius: 3,
          bgcolor: surface,
          border: `1px solid ${border}`,
          boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
        }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.4} alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
            <Chip icon={<InfoOutlinedIcon />} label="反映中" variant="outlined" />
            {liveSummary ? (
              <>
                <Chip label={`required: ${liveSummary.required}`} />
                <Chip label={`minSupported: ${liveSummary.minSupported}`} />
                <Chip label={`rollout: ${liveSummary.rollout}`} />
                <Chip label={`allow: ${liveSummary.allow}`} />
                <Chip label={`deny: ${liveSummary.deny}`} />
              </>
            ) : (
              <Chip label="未作成（初回保存で作成されます）" />
            )}
          </Stack>

          <Box sx={{ flexGrow: 1 }} />
          {dirty && <Chip label="未保存の変更があります" color="warning" />}
        </Stack>
      </Paper>

      {/* フォーム */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2.5 },
          borderRadius: 3,
          bgcolor: surface,
          border: `1px solid ${border}`,
          boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
        }}
      >
        {loading ? (
          <Box sx={{ py: 5, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2.1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: textBase }}>
              バージョン閾値
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.6}>
              <TextField
                label="requiredBuildId"
                value={form.requiredBuildId ?? ""}
                onChange={(e) => setField("requiredBuildId", e.target.value)}
                helperText={`自動算出: requiredBuildCode = ${buildCodeFromId(form.requiredBuildId)}`}
                fullWidth
              />
              <TextField
                label="minSupportedBuildId"
                value={form.minSupportedBuildId ?? ""}
                onChange={(e) => setField("minSupportedBuildId", e.target.value)}
                helperText={`自動算出: minSupportedBuildCode = ${buildCodeFromId(form.minSupportedBuildId)}`}
                fullWidth
              />
            </Stack>

            <Divider />

            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: textBase }}>
              ロールアウト
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.6} alignItems={{ sm: "center" }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(form?.rollout?.enabled)}
                    onChange={(e) => setField("rollout.enabled", e.target.checked)}
                  />
                }
                label="rollout.enabled"
              />

              <TextField
                label="rollout.percent (0-100)"
                type="number"
                value={form?.rollout?.percent ?? 100}
                onChange={(e) => setField("rollout.percent", clampPercent(e.target.value))}
                sx={{ width: { xs: "100%", sm: 240 } }}
                inputProps={{ min: 0, max: 100, step: 1 }}
                helperText="段階導入用。100で全員対象。"
              />
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.6}>
              <TextField
                label="rollout.allowUids（1行1UID / カンマ区切り可）"
                value={uidListToText(form?.rollout?.allowUids)}
                onChange={(e) => setField("rollout.allowUids", normalizeUidList(e.target.value))}
                fullWidth
                multiline
                minRows={4}
              />
              <TextField
                label="rollout.denyUids（1行1UID / カンマ区切り可）"
                value={uidListToText(form?.rollout?.denyUids)}
                onChange={(e) => setField("rollout.denyUids", normalizeUidList(e.target.value))}
                fullWidth
                multiline
                minRows={4}
              />
            </Stack>

            <Divider />

            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: textBase }}>
              表示文言（クライアント側のアップデート促しDialog用）
            </Typography>

            <TextField
              label="message.title"
              value={form?.message?.title ?? ""}
              onChange={(e) => setField("message.title", e.target.value)}
              fullWidth
            />
            <TextField
              label="message.body"
              value={form?.message?.body ?? ""}
              onChange={(e) => setField("message.body", e.target.value)}
              fullWidth
              multiline
              minRows={3}
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.6}>
              <TextField
                label="message.primaryText"
                value={form?.message?.primaryText ?? ""}
                onChange={(e) => setField("message.primaryText", e.target.value)}
                fullWidth
              />
              <TextField
                label="message.secondaryText"
                value={form?.message?.secondaryText ?? ""}
                onChange={(e) => setField("message.secondaryText", e.target.value)}
                fullWidth
              />
            </Stack>

            <Alert severity="info">
              required/minSupported は buildId（文字列）ではなく buildCode（数値）で比較する設計にしておくと、
              ルール・クライアント両方で事故が減ります。buildCode はこの画面で自動算出します。
            </Alert>
          </Stack>
        )}
      </Paper>
    </Box>
  );
}

/** -------- util: deep merge（既定値補完用） -------- */
function deepMerge(base, extra) {
  if (!extra) return { ...base };
  const out = Array.isArray(base) ? [...base] : { ...base };

  for (const k of Object.keys(extra || {})) {
    const bv = base?.[k];
    const ev = extra?.[k];

    if (Array.isArray(ev)) {
      out[k] = ev;
      continue;
    }
    if (ev && typeof ev === "object" && !Array.isArray(ev)) {
      out[k] = deepMerge(bv && typeof bv === "object" ? bv : {}, ev);
      continue;
    }
    out[k] = ev;
  }
  return out;
}

function setByPath(obj, path, value) {
  const keys = String(path).split(".");
  const out = { ...(obj || {}) };
  let cur = out;

  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    const v = cur[k];
    cur[k] = v && typeof v === "object" && !Array.isArray(v) ? { ...v } : {};
    cur = cur[k];
  }

  cur[keys[keys.length - 1]] = value;
  return out;
}

/**
 * buildDistribution の byBuildId を表示用に正規化する
 *
 * 対応したいケース：
 * 1) ネスト形式（FieldPath で書かれている本来の形）
 *    {
 *      byBuildId: {
 *        "2": { "0": 10927, "1": 142 },
 *        "unknown": 0
 *      }
 *    }
 *
 * 2) 本当にトップレベルにフラットキーが来てしまう形（稀だがUI側で吸収）
 *    {
 *      "byBuildId.2.0": 10927,
 *      "byBuildId.2.1": 142,
 *      totalMoves: 11155
 *    }
 *
 * どちらでも out は { "2.0": 10927, "2.1": 142, "unknown": 0 } になる。
 */
function normalizeBuildDistribution(metricsDoc) {
  const out = {};
  const isPlainObj = (v) => v && typeof v === "object" && !Array.isArray(v);

  const add = (key, value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return;
    out[key] = (out[key] || 0) + n;
  };

  // (A) “フラットキー” を拾う: "byBuildId.2.0" -> "2.0"
  for (const [k, v] of Object.entries(metricsDoc || {})) {
    if (k.startsWith("byBuildId.")) {
      add(k.replace(/^byBuildId\./, ""), v);
    }
  }

  // (B) “ネスト” を拾う: byBuildId: { "2": { "0": 10927 } }
  const by = metricsDoc?.byBuildId;
  if (isPlainObj(by)) {
    for (const [k, v] of Object.entries(by)) {
      if (typeof v === "number" && Number.isFinite(v)) {
        // byBuildId: { unknown: 1 } のようなケース
        add(k, v);
        continue;
      }
      if (isPlainObj(v)) {
        for (const [k2, v2] of Object.entries(v)) {
          add(`${k}.${k2}`, v2);
        }
      }
    }
  }

  return out;
}
